import type { Skill } from '@atlassian/rovo-platform-types/skills';
import { resolveSkillDisplayDescription } from '@atlassian/rovo-platform-ui-components/skills/resolve-skill-display-description';
import type { SkillIconName } from '@atlassian/rovo-platform-ui-components/skills/skill-icons';

/**
 * build-skill's ARI is stable and identical across every site, unlike a custom skill's ARI, which
 * is generated per site. Safe to hardcode.
 */
export const BUILD_SKILL_ARI = 'ari:cloud:platform::skill/184b743e-95db-4069-a479-f271c3e49bdb';

export type BuildSkillProbeItem = {
	readonly description: string;
	readonly id: string;
	readonly name: string;
	readonly metadata: {
		readonly tags: ReadonlyArray<string>;
	};
	readonly uiMetadata: {
		readonly color?: string | null;
		readonly displayName: string;
		readonly helpText?: string | null;
		readonly iconKey?: string | null;
		readonly iconUrl?: string | null;
	};
} | null;

/**
 * Maps the `graphIntegration_skillItems` hydration response (a direct lookup by ARI, not a search)
 * onto a `Skill`. `item.name` is already the kebab-case machine identifier (e.g. `build-skill`),
 * not a display name — that lives in `uiMetadata.displayName`.
 */
export const parseBuildSkillProbe = (
	items: ReadonlyArray<BuildSkillProbeItem> | null | undefined,
): Skill | undefined => {
	const item = items?.find((candidate) => candidate != null);
	if (!item) {
		return undefined;
	}

	return {
		id: item.id,
		slug: item.name,
		name: item.uiMetadata.displayName,
		description: item.description,
		helpText: resolveSkillDisplayDescription(item.uiMetadata.helpText, item.description),
		iconName: (item.uiMetadata.iconKey ||
			item.uiMetadata.iconUrl?.split('name/')?.[1]) as SkillIconName,
		...(item.uiMetadata.color ? { color: item.uiMetadata.color as Skill['color'] } : {}),
		...(item.metadata.tags.length > 0 ? { tags: [...item.metadata.tags] } : {}),
	};
};
